import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type FormEvent,
  type ReactNode,
} from 'react';
import { cn } from '../lib/utils';
import { Badge } from './Badge';
import { Spinner } from './ui/spinner';

export type QuestionnaireQuestion = {
  name: string;
  prompt: string;
  description?: string;
  required?: boolean;
  multiple?: boolean;
  choices: { value: string; label: string; description?: string; recommended?: boolean }[];
  /**
   * Hide this question when every selected value of an earlier answer is one of these.
   * A multi-select that also includes another value still shows the question.
   */
  skipIf?: { name: string; values: string[] };
};

/** Questions that still apply, given what the operator has picked so far. */
export function applicableQuestions<T extends QuestionnaireQuestion>(
  questions: readonly T[],
  picked: Record<string, string[]>,
): T[] {
  return questions.filter((question) => {
    const rule = question.skipIf;
    if (!rule) return true;
    const values = picked[rule.name] ?? [];
    return values.length === 0 || values.some((value) => !rule.values.includes(value));
  });
}

type QuestionnaireFormProps = {
  questions: QuestionnaireQuestion[];
  onComplete: (answers: string[][]) => void;
  className?: string;
  /** Shown at the end of each question's title line, such as where the question sits in a flow. */
  aside?: ReactNode;
};

const QuestionnaireForm = lazy(async () => {
  const {
    Questionnaire,
    QuestionnaireProgress,
    QuestionnaireItem,
    QuestionnaireTitle,
    QuestionnaireDescription,
    QuestionnaireChoices,
    QuestionnaireChoice,
    QuestionnaireError,
    QuestionnaireActions,
    QuestionnairePrevious,
    QuestionnaireSkip,
    QuestionnaireNext,
    QuestionnaireSubmit,
  } = await import('./ui/questionnaire');

  function Item(props: ComponentProps<typeof QuestionnaireItem>) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const item = ref.current?.querySelector('fieldset');
      if (item instanceof HTMLElement) item.focus();
    }, []);

    return (
      <div className="contents" ref={ref}>
        <QuestionnaireItem {...props} />
      </div>
    );
  }

  function Form({ questions, onComplete, className, aside }: QuestionnaireFormProps) {
    const [picked, setPicked] = useState<Record<string, string[]>>({});
    const shown = applicableQuestions(questions, picked);

    // The questionnaire does not report the current picks. Read them from the form so a
    // skipIf question can leave the card as soon as the earlier answer matches.
    const remember = (event: FormEvent<HTMLFormElement>) => {
      const data = new FormData(event.currentTarget);
      setPicked(
        Object.fromEntries(
          questions.map((question) => [
            question.name,
            data.getAll(question.name).map(String).filter(Boolean),
          ]),
        ),
      );
    };

    const submit = (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      onComplete(
        questions.map((question) => data.getAll(question.name).map(String).filter(Boolean)),
      );
    };

    return (
      <Questionnaire
        onChange={remember}
        className={cn(
          'w-[26rem] max-w-full gap-2 rounded-lg border border-border bg-background-surface p-3 text-sm outline-none [&_[hidden]]:!hidden',
          className,
        )}
        items={shown.map((question) => ({
          name: question.name,
          required: question.required ?? true,
          choices: question.choices.map((choice) => ({ value: choice.value })),
        }))}
        shortcuts="numbers"
        onSubmit={submit}
        // A click on the card's text would leave focus on the page, out of reach of the shortcuts
        // and arrow keys. The form takes that focus and hands it to the open question.
        tabIndex={-1}
        onFocus={(event) => {
          if (event.target !== event.currentTarget) return;
          event.currentTarget.querySelector<HTMLElement>('fieldset:not([hidden])')?.focus();
        }}
      >
        {shown.length > 1 && <QuestionnaireProgress className="text-xs text-muted-foreground" />}
        {shown.map((question) => (
          <Item
            key={question.name}
            multiple={question.multiple}
            name={question.name}
            required={question.required ?? true}
          >
            <QuestionnaireTitle className="mb-1 flex w-full items-baseline gap-3 text-sm font-medium">
              <span className="flex-1">{question.prompt}</span>
              {aside}
            </QuestionnaireTitle>
            {(question.description || question.multiple) && (
              <QuestionnaireDescription className="text-xs text-muted-foreground">
                {question.multiple && (
                  <span className="font-medium text-foreground">Select all that apply. </span>
                )}
                {question.description}
              </QuestionnaireDescription>
            )}
            <QuestionnaireChoices className="gap-0.5">
              {question.choices.map((choice) => (
                <QuestionnaireChoice
                  className={cn(
                    'relative min-h-0 border-0 bg-transparent px-1 py-1 hover:bg-background-muted data-[checked]:bg-background-elevated data-[checked]:ring-1 data-[checked]:ring-foreground-accent data-[checked]:[&_[data-slot=questionnaire-choice-shortcut]]:border-foreground-accent data-[checked]:[&_[data-slot=questionnaire-choice-shortcut]]:text-foreground-accent dark:[&_[data-slot=questionnaire-choice-shortcut]]:border-border dark:[&_[data-slot=questionnaire-choice-shortcut]]:bg-background-elevated dark:[&_[data-slot=questionnaire-choice-shortcut]]:text-foreground',
                    // Single choice relies on the highlight alone. Multiple choice shows checkboxes.
                    question.multiple
                      ? 'dark:data-[unchecked]:[&_[data-slot=questionnaire-choice-indicator]]:bg-background-surface'
                      : '[&_[data-slot=questionnaire-choice-indicator]]:hidden',
                  )}
                  key={choice.value}
                  value={choice.value}
                >
                  <span>
                    {choice.label}
                    {choice.recommended && (
                      <Badge className="ms-2" size="compact" variant="primary">
                        Recommended
                      </Badge>
                    )}
                  </span>
                  {choice.description && (
                    <span className="text-xs text-muted-foreground">{choice.description}</span>
                  )}
                </QuestionnaireChoice>
              ))}
            </QuestionnaireChoices>
            <QuestionnaireError />
          </Item>
        ))}
        <QuestionnaireActions className="mt-1 flex justify-end gap-2">
          <QuestionnairePrevious className="mr-auto" size="sm" variant="ghost" />
          <QuestionnaireSkip size="sm" variant="ghost">
            Skip
          </QuestionnaireSkip>
          <QuestionnaireNext size="sm" variant="default">
            Continue
          </QuestionnaireNext>
          <QuestionnaireSubmit size="sm" variant="default">
            Continue
          </QuestionnaireSubmit>
        </QuestionnaireActions>
      </Questionnaire>
    );
  }

  return { default: Form };
});

/** Letter shortcuts, one question at a time. The shadcn package loads only when this mounts. */
export function Questionnaire(props: QuestionnaireFormProps) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center p-3">
          <Spinner />
        </div>
      }
    >
      <QuestionnaireForm {...props} />
    </Suspense>
  );
}
